/**
 * HistorySheet —— 设备历史曲线抽屉（遥测数据）。
 * 指标来自设备 spec 中的只读能力；时间范围 1 / 6 / 24 小时。
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Capability, Device } from '@znjj/shared';
import { deviceKeys, devicesApi } from '../api/devices';
import { Icon } from './Icon';
import { fmt, READ_LABEL, Sheet, Sparkline } from './ui';

const RANGES = [1, 6, 24] as const;

function readableCaps(device: Device): Capability[] {
  return device.spec.capabilities.filter(
    (c) => c.access === 'r' && device.state[c.code] !== undefined && device.state[c.code] !== null,
  );
}

export function HistorySheet({
  device,
  open,
  onClose,
}: {
  device: Device;
  open: boolean;
  onClose: () => void;
}) {
  const caps = readableCaps(device);
  const [code, setCode] = useState(caps[0]?.code ?? '');
  const [hours, setHours] = useState<number>(24);

  const cap = caps.find((c) => c.code === code);
  const dec = cap?.type === 'float' ? 1 : 0;

  const query = useQuery({
    queryKey: deviceKeys.telemetry(device.id, code, hours),
    queryFn: () => devicesApi.telemetry(device.id, code, hours),
    enabled: open && !!code,
  });

  const points = query.data?.points ?? [];
  const vals = points.map((p) => Number(p.v));
  const stats = vals.length
    ? {
        min: Math.min(...vals),
        max: Math.max(...vals),
        cur: vals[vals.length - 1],
      }
    : null;

  return (
    <Sheet open={open} onClose={onClose} title={`${device.name} · 历史数据`}>
      <div className="stack" style={{ gap: 14 }}>
        <div>
          <div className="field__label" style={{ marginBottom: 7 }}>
            指标
          </div>
          <div className="row row--wrap">
            {caps.map((c) => (
              <button
                key={c.code}
                className={'chip' + (code === c.code ? ' is-on' : '')}
                type="button"
                onClick={() => setCode(c.code)}
              >
                {READ_LABEL[c.code] ?? c.code}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="field__label" style={{ marginBottom: 7 }}>
            时间范围
          </div>
          <div className="seg">
            {RANGES.map((r) => (
              <button
                key={r}
                className={'seg__btn' + (hours === r ? ' is-active' : '')}
                type="button"
                onClick={() => setHours(r)}
              >
                {r} 小时
              </button>
            ))}
          </div>
        </div>

        <div className="card card--flat" style={{ padding: 12 }}>
          {query.isPending ? (
            <div className="skeleton" style={{ height: 44 }} />
          ) : points.length ? (
            <Sparkline points={points} label={`${READ_LABEL[code] ?? code} 趋势`} />
          ) : (
            <div className="empty" style={{ padding: '20px 0' }}>
              <span className="empty__ico">
                <Icon name="chart-line" size={20} />
              </span>
              <div className="empty__title">暂无该指标的历史数据</div>
            </div>
          )}
          <div className="row row--between t-xs dim" style={{ marginTop: 6 }}>
            <span>{points.length ? fmt.hhmm(points[0].t) : '—'}</span>
            <span>{points.length ? fmt.hhmm(points[points.length - 1].t) : '—'}</span>
          </div>
        </div>

        {stats ? (
          <div className="grid-metrics">
            {(
              [
                ['最低', stats.min],
                ['最高', stats.max],
                ['当前', stats.cur],
              ] as const
            ).map(([label, v]) => (
              <div className="metric" key={label}>
                <span className="metric__num">
                  {fmt.num(v, dec)}
                  {cap?.unit ? <span className="metric__unit">{cap.unit}</span> : null}
                </span>
                <span className="metric__cap">{label}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}
